/* istanbul ignore file */

import { Module } from 'vuex';
import { AppState } from '..';
import { prefixValues } from '@/helpers/util';
import { OrderConfirmationCreateAccountExperience, SelectableVariantMenuItem, TopLevelMenuGroup, UpsellItem } from '@/models';
import { AdobeTargetResponsePersonalizationPayload, UtmParameters } from '@/models/adobe-target';

export interface AdobeTargetModuleState {
  reorderedTopLevelMenu: TopLevelMenuGroup[] | null;
  utmParameters: UtmParameters | null;
  isFeaturedSectionAboveTopLevelMenu: boolean;
  mealBuilderProteinSortOrder: SelectableVariantMenuItem[];
  ecid: string;
  orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience | null;
  personalizedUpsellCmgIds: string[];
  displayedUpsellItems: UpsellItem[];
  propositionDisplayData: Map<string, AdobeTargetResponsePersonalizationPayload | null>;
}

const modulePrefix = 'adobeTarget/';
const MUTATION_NAMES = {
  SET_TOP_LEVEL_MENU_REORDERING: 'setReorderedTopLevelMenu',
  CLEAR_TOP_LEVEL_MENU_REORDERING: 'clearReorderedTopLevelMenu',
  SET_UTM_PARAMETERS: 'setUtmParameters',
  SET_FEATURED_SECTION_ABOVE_TOP_LEVEL_MENU: 'setFeaturedSectionAboveTopLevelMenu',
  SET_MEAL_BUILDER_PROTEIN_SORT_ORDER: 'setfMealBuilderProteinSortOrder',
  SET_CUSTOMER_ECID: 'setCustomerECID',
  SET_ORDER_CONFIRMATION_CREATE_ACCOUNT_EXPEREINCE: 'setOrderConfirmationCreateAccountExperience',
  SET_PERSONALIZED_UPSELL_CMG_IDS: 'setPersonalizedUpsellCmgIds',
  SET_DISPLAYED_UPSELL_ITEMS: 'setDisplayedUpsellItems',
  SET_PROPOSITION_DISPLAY_DATA: 'setPropositionDisplayData',
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, modulePrefix);

export function makeAdobeTargetModule(): Module<AdobeTargetModuleState, AppState> {
  return {
    namespaced: true,
    state: {
      reorderedTopLevelMenu: null,
      utmParameters: null,
      isFeaturedSectionAboveTopLevelMenu: false,
      mealBuilderProteinSortOrder: [],
      ecid: '',
      orderConfirmationCreateAccountExperience: null,
      personalizedUpsellCmgIds: [],
      displayedUpsellItems: [],
      propositionDisplayData: new Map<string, AdobeTargetResponsePersonalizationPayload>(),
    },
    mutations: {
      [MUTATION_NAMES.SET_TOP_LEVEL_MENU_REORDERING]: (state, reorderedTopLevelMenu: TopLevelMenuGroup[]) => {
        state.reorderedTopLevelMenu = reorderedTopLevelMenu;
      },
      [MUTATION_NAMES.CLEAR_TOP_LEVEL_MENU_REORDERING]: (state) => {
        state.reorderedTopLevelMenu = null;
      },
      [MUTATION_NAMES.SET_UTM_PARAMETERS]: (state, utmParameters: UtmParameters) => {
        state.utmParameters = utmParameters;
      },
      [MUTATION_NAMES.SET_FEATURED_SECTION_ABOVE_TOP_LEVEL_MENU]: (state, isFeaturedSectionAboveTopLevelMenu: boolean) => {
        state.isFeaturedSectionAboveTopLevelMenu = isFeaturedSectionAboveTopLevelMenu;
      },
      [MUTATION_NAMES.SET_MEAL_BUILDER_PROTEIN_SORT_ORDER]: (state, mealBuilderProteinsSortOrder: SelectableVariantMenuItem[]) => {
        state.mealBuilderProteinSortOrder = mealBuilderProteinsSortOrder;
      },
      [MUTATION_NAMES.SET_CUSTOMER_ECID]: (state, ecid: string) => {
        state.ecid = ecid;
      },
      [MUTATION_NAMES.SET_ORDER_CONFIRMATION_CREATE_ACCOUNT_EXPEREINCE]: (state, orderConfirmationCreateAccountExperience: OrderConfirmationCreateAccountExperience) => {
        state.orderConfirmationCreateAccountExperience = orderConfirmationCreateAccountExperience;
      },
      [MUTATION_NAMES.SET_PERSONALIZED_UPSELL_CMG_IDS]: (state, personalizedUpsellCmgIds: string[]) => {
        state.personalizedUpsellCmgIds = personalizedUpsellCmgIds;
      },
      [MUTATION_NAMES.SET_DISPLAYED_UPSELL_ITEMS]: (state, displayedUpsellItems: UpsellItem[]) => {
        state.displayedUpsellItems = displayedUpsellItems;
      },
      [MUTATION_NAMES.SET_PROPOSITION_DISPLAY_DATA]: (state, propositionDisplayData: AdobeTargetResponsePersonalizationPayload | null) => {
        if (propositionDisplayData?.scope) {
          state.propositionDisplayData.set(propositionDisplayData.scope, propositionDisplayData);
        }
      }
    },
  };
}
